import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { BlogPost, User } from '../types';
import { fetchBlogBySlug, fetchUser } from '../services/api';


const ArticleView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [author, setAuthor] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setError('No article slug provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch the blog post by slug
        const blog = await fetchBlogBySlug(slug);
        if (!blog) {
          setError('Article not found');
          setPost(null);
          setLoading(false);
          return;
        }

        // Try to fetch user info, but don't fail if it doesn't work
        try {
          const user = await fetchUser(blog.author_username);
          setAuthor(user);
        } catch (userError) {
          console.warn(`Could not fetch user info for ${blog.author_username}:`, userError);
          // Keep author as null, will fall back to author_username
        }

        setPost(blog);
        setError(null);
      } catch (err) {
        console.error('Failed to load article:', err);
        setError('Failed to load article. Please try again later.');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="article-view">
        <div className="article-container">
          <div className="loading-state">Loading article...</div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="article-view">
        <div className="article-container">
          <div className="article-not-found">
            <h1>Article not found</h1>
            <p>{error || "The article you're looking for doesn't exist."}</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }
  const formatDate = (dateStr: string) => {
    const dateObj = new Date(dateStr);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(dateObj);
  };

  const formatReadTime = (minutes: number) => {
    return `${minutes} min read`;
  };

  // ...existing code...

  return (
    <div className="article-view">
      <div className="article-container">
        <header className="article-header">
          <div className="article-meta">
            {post.title && (
              <h1 className="article-title">{post.title}</h1>
            )}
            {post.subtitle && (
              <p className="article-subtitle">{post.subtitle}</p>
            )}
            
            <div className="article-author-info">
              <div className="author-details" onClick={() => navigate(`/users/${author?.username || post.author_username}`)} style={{ cursor: 'pointer' }}>
                <div className="author-avatar">
                  {(author?.name || post.author_username).charAt(0).toUpperCase()}
                </div>
                <div className="author-text">
                  <div className="author-name">{author?.name || post.author_username}</div>
                  {author?.username && <div className="author-username">@{author.username}</div>}
                </div>
              </div>
              
              <div className="article-metadata">
                <span className="article-date">{formatDate(post.posted_at)}</span>
                <span className="metadata-separator">•</span>
                <span className="metadata-separator">•</span>
              </div>
            </div>
          </div>
        </header>

        <main className="article-content">
          <div className="article-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
            >
              {post.content}
            </ReactMarkdown>
          </div>
        </main>

        <footer className="article-footer">
          <div className="article-actions">
            <button className="action-button share-button">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M15 6.66667L10 11.6667L5 6.66667" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
                <path 
                  d="M5 6.66667H15C16.1046 6.66667 17 7.56214 17 8.66667V15C17 16.1046 16.1046 17 15 17H5C3.89543 17 3 16.1046 3 15V8.66667C3 7.56214 3.89543 6.66667 5 6.66667Z" 
                  stroke="currentColor" 
                  strokeWidth="2"
                />
                <path 
                  d="M10 3V6.66667" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round"
                />
              </svg>
              Share
            </button>
          </div>
        </footer>

        <aside className="author-bio-section">
          <div className="author-bio-card" onClick={() => navigate(`/users/${author?.username || post.author_username}`)} style={{ cursor: 'pointer' }}>
            <div className="bio-avatar">
              {(author?.name || post.author_username).charAt(0).toUpperCase()}
            </div>
            <div className="bio-content">
              <h3 className="bio-name">{author?.name || post.author_username}</h3>
              {author?.username && <p className="bio-username">@{author.username}</p>}
              <p className="bio-description">{author?.quote || "Blog Writer"}</p>
              <button className="follow-button" onClick={(e) => e.stopPropagation()}>Follow</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default ArticleView;